import { useLayoutEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import categoryBracelet from "@/assets/categories-generated/optimized/category-bracelet.webp";
import categoryCombo from "@/assets/categories-generated/optimized/category-combo.webp";
import categoryMala from "@/assets/categories-generated/optimized/category-mala.webp";
import categoryRudraksha from "@/assets/categories-generated/optimized/category-rudraksha.webp";
import categoryGemstone from "@/assets/categories-generated/optimized/category-gemstone.webp";
import categoryNakshatra from "@/assets/categories-generated/optimized/category-nakshatra.webp";
import categoryRasi from "@/assets/categories-generated/optimized/category-rasi.webp";
import categoryYantra from "@/assets/categories-generated/optimized/category-yantra.webp";
import categoryPyrite from "@/assets/categories-generated/optimized/category-pyrite.webp";
import categoryIdol from "@/assets/categories-generated/optimized/category-idol.webp";
import categoryProducts from "@/assets/categories-generated/optimized/category-others.webp";
import { getCategoryPath, PRODUCT_CATEGORIES } from "@/lib/productCategories";

const CATEGORY_IMAGES: Record<string, string> = {
  bracelet: categoryBracelet, combo: categoryCombo, mala: categoryMala, rudraksha: categoryRudraksha,
  gemstone: categoryGemstone, nakshatra: categoryNakshatra, yantra: categoryYantra, pyrite: categoryPyrite,
  idol: categoryIdol, rasi: categoryRasi, others: categoryProducts,
};

const CATEGORIES = PRODUCT_CATEGORIES.map((category) => ({
  ...category,
  image: CATEGORY_IMAGES[category.key],
}));

const CategoryIcons = () => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const firstCopyRef = useRef<HTMLDivElement>(null);
  const hoveredRef = useRef(false);
  const touchingRef = useRef(false);
  const pauseUntilRef = useRef(0);
  const dimensionsRef = useRef({ copyWidth: 0, rightWrap: 0 });
  const visibleRef = useRef(false);

  const pauseAutoScroll = () => {
    pauseUntilRef.current = performance.now() + 3000;
  };

  const wrapScroll = () => {
    const scroller = scrollerRef.current;
    const { copyWidth, rightWrap } = dimensionsRef.current;
    if (!scroller || !copyWidth) return;
    if (scroller.scrollLeft <= 0) scroller.scrollLeft += copyWidth;
    else if (scroller.scrollLeft >= rightWrap - 1) scroller.scrollLeft -= copyWidth;
  };

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const firstCopy = firstCopyRef.current;
    if (!scroller || !firstCopy) return;
    const measure = () => {
      const copyWidth = firstCopy.offsetWidth;
      dimensionsRef.current = {
        copyWidth,
        rightWrap: Math.min(copyWidth * 2, scroller.scrollWidth - scroller.clientWidth),
      };
    };
    measure();
    scroller.scrollLeft = dimensionsRef.current.copyWidth;
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(scroller);
    resizeObserver.observe(firstCopy);
    const visibilityObserver = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
    });
    visibilityObserver.observe(scroller);

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let previousTime = performance.now();
    const advance = (time: number) => {
      const elapsed = Math.min(time - previousTime, 64);
      previousTime = time;
      if (visibleRef.current && document.visibilityState === "visible" && !reducedMotion.matches && !hoveredRef.current && !touchingRef.current
        && !scroller.contains(document.activeElement) && time >= pauseUntilRef.current) {
        scroller.scrollLeft += (dimensionsRef.current.copyWidth / 40000) * elapsed;
        wrapScroll();
      }
      frame = window.requestAnimationFrame(advance);
    };
    frame = window.requestAnimationFrame(advance);
    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
    };
  }, []);

  const moveManually = (direction: -1 | 1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    pauseAutoScroll();
    scroller.scrollBy({ left: direction * (window.innerWidth < 768 ? 168 : 300), behavior: "smooth" });
  };

  return (
    <section aria-label="Shop by category" className="relative bg-white py-2 md:py-3">
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-white to-transparent md:w-16" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-white to-transparent md:w-16" />

      <button type="button" aria-label="Scroll categories left" onClick={() => moveManually(-1)}
        className="absolute left-2 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:flex">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button type="button" aria-label="Scroll categories right" onClick={() => moveManually(1)}
        className="absolute right-2 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:flex">
        <ChevronRight className="h-5 w-5" />
      </button>

      <div ref={scrollerRef} aria-label="Categories, swipe left or right to browse"
        className="overflow-x-auto overscroll-x-contain scrollbar-none md:mx-auto"
        onScroll={wrapScroll}
        onWheel={pauseAutoScroll}
        onPointerEnter={(event) => { if (event.pointerType === "mouse") hoveredRef.current = true; }}
        onPointerLeave={(event) => { if (event.pointerType === "mouse") { hoveredRef.current = false; pauseAutoScroll(); } }}
        onTouchStart={() => { touchingRef.current = true; }}
        onTouchEnd={() => { touchingRef.current = false; pauseAutoScroll(); }}
        onTouchCancel={() => { touchingRef.current = false; pauseAutoScroll(); }}>
        <div className="flex w-max">
          {[0, 1, 2].map((copy) => (
            <div key={copy} ref={copy === 0 ? firstCopyRef : undefined}
              aria-hidden={copy === 1 ? undefined : true}
              className="flex shrink-0 gap-3 px-1.5 md:gap-4 md:px-2">
              {CATEGORIES.map((category) => (
                <Link key={category.key} to={getCategoryPath(category.key)}
                  tabIndex={copy === 1 ? undefined : -1}
                  className="group flex min-w-[72px] shrink-0 flex-col items-center gap-1.5 md:min-w-[126px] md:gap-2">
                  <div className="h-[72px] w-[72px] overflow-hidden rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 shadow-sm transition-all group-hover:shadow-md md:h-[126px] md:w-[126px] md:group-hover:scale-105">
                    <img src={category.image} alt={`${category.label} category`}
                      className="h-full w-full object-cover" loading="lazy" decoding="async" />
                  </div>
                  <span className="text-center text-[11px] font-medium leading-tight text-foreground transition-colors group-hover:text-primary md:text-xs">
                    {category.label}
                  </span>
                </Link>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CategoryIcons;
